// Illustration asset loader with graceful fallback chain.
//
// Assets live in frontend/public/illustrations/ and are served statically
// by Vite, so they are referenced by absolute URL path. When a PNG is
// missing (the parent hasn't baked that asset yet), the loader returns
// null and the consuming component falls back to its procedural default.
//
// The manifest encodes "which assets we *expect* to exist" — the actual
// on-disk check happens via an Image onerror handler in the consumer.

export type AdventurerId = 'finn' | 'frida' | 'otto' | 'octi';
export type AdventurerPose =
  | 'idle' | 'thinking' | 'excited' | 'encouraging'
  | 'confused' | 'celebrating' | 'asleep' | 'reading';

// Matches the arc ids in curriculum/story_arcs.py.
export type ArcId =
  | 'schatzsuche'
  | 'zaubermarkt'
  | 'drachenhoehle'
  | 'weiser_affe'
  | 'grosszuegiger_loewe'
  | 'kluge_kraehe'
  | 'mutige_schildkroete'
  | 'listiger_schakal';

const ADVENTURER_IDS: AdventurerId[] = ['finn', 'frida', 'otto', 'octi'];
const POSES: AdventurerPose[] = [
  'idle', 'thinking', 'excited', 'encouraging',
  'confused', 'celebrating', 'asleep', 'reading',
];
const ARC_IDS: ArcId[] = [
  'schatzsuche', 'zaubermarkt', 'drachenhoehle', 'weiser_affe',
  'grosszuegiger_loewe', 'kluge_kraehe', 'mutige_schildkroete', 'listiger_schakal',
];
const CHAPTERS_PER_ARC = 3;

export function adventurerAssetUrl(id: AdventurerId, pose: AdventurerPose): string {
  return `/illustrations/adventurers/${id}__${pose}.png`;
}

export function chapterBadgeUrl(arcId: string): string {
  return `/illustrations/badges/${arcId}.png`;
}

/** Arc scene background (one per chapter of an arc). */
export function sceneBackgroundUrl(arcId: string, chapterIndex: number): string {
  const idx = Math.max(0, Math.min(CHAPTERS_PER_ARC - 1, Math.floor(chapterIndex)));
  return `/illustrations/scenes/${arcId}__${idx}.png`;
}

/**
 * Semantic beat → adventurer pose map. Covers the 4 in-exercise beats plus
 * session-flow states (chapter complete, explainer, session end, stuck).
 */
export type Beat =
  | 'render' | 'correct' | 'incorrect' | 'idle'
  | 'celebrating' | 'reading' | 'asleep' | 'confused';

export function poseForBeat(beat: Beat): AdventurerPose {
  switch (beat) {
    case 'render':       return 'thinking';
    case 'correct':      return 'excited';
    case 'incorrect':    return 'encouraging';
    case 'celebrating':  return 'celebrating';
    case 'reading':      return 'reading';
    case 'asleep':       return 'asleep';
    case 'confused':     return 'confused';
    case 'idle':
    default:             return 'idle';
  }
}

/** Full list of every expected asset — used by tests and the bake preflight. */
export function expectedManifest(): {
  adventurers: { id: AdventurerId; pose: AdventurerPose; path: string }[];
  badges: { arcId: ArcId; path: string }[];
  scenes: { arcId: ArcId; chapterIndex: number; path: string }[];
} {
  const adventurers = ADVENTURER_IDS.flatMap(id =>
    POSES.map(pose => ({ id, pose, path: adventurerAssetUrl(id, pose) })),
  );
  const badges = ARC_IDS.map(arcId => ({ arcId, path: chapterBadgeUrl(arcId) }));
  const scenes = ARC_IDS.flatMap(arcId =>
    Array.from({ length: CHAPTERS_PER_ARC }, (_, i) => ({
      arcId, chapterIndex: i, path: sceneBackgroundUrl(arcId, i),
    })),
  );
  return { adventurers, badges, scenes };
}

/**
 * Cache of URLs we've already probed, keyed by URL.
 * True = image loads. False = 404 / error. undefined = not yet probed.
 */
const _probeCache = new Map<string, boolean>();

/** Resolve whether an asset actually exists on disk. Caches per URL. */
export function probeImage(url: string): Promise<boolean> {
  const cached = _probeCache.get(url);
  if (cached !== undefined) return Promise.resolve(cached);
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => { _probeCache.set(url, true); resolve(true); };
    img.onerror = () => { _probeCache.set(url, false); resolve(false); };
    img.src = url;
  });
}
